import { Meta } from './meta.model'

// Um documento de lei no acervo do microserviço de IA.
//
// É a entrada autoritativa: a norma chega inteira e é separada em dispositivos
// conferindo cada trecho contra o próprio documento. O outro caminho — a busca
// na internet, disparada quando uma questão cita norma que o acervo não tem —
// traz só os artigos que decidem aquela questão, transcritos sem como conferir.
// Subir o documento é o que troca o recorte pela lei.
//
// A carga é síncrona: o envio devolve o relatório e a norma já está guardada ou
// já falhou com o motivo. Não há status a acompanhar depois.

// De onde o texto da norma veio, e o quanto ele vale.
//
// `oficial` tem captura e literalidade conferida contra o documento, e a
// situação do dispositivo lida na própria fonte. `web` tem captura e
// literalidade, mas a situação ninguém conferiu: é a súmula que a conversão
// achou numa página de tribunal quando o acervo não a tinha, e a página que a
// publica costuma ser a notícia da aprovação — ela não diz se a súmula foi
// cancelada depois. `dls` é recorte vindo das fundamentações que a curadoria
// aprovou, do tamanho da questão que o originou.
//
// São três níveis de confiança e a tela não pode esconder um no outro. Foi o
// que aconteceu quando `web` apareceu: com o rótulo binário, a norma capturada
// na web era exibida como "Recorte do DLS", que é justamente o que ela não é.
export type LegalDocumentOrigin = 'oficial' | 'web' | 'dls'

// Como o selo da origem se lê, num lugar só.
//
// Duas telas mostram isso — a lista e o documento aberto —, e o julgamento é o
// mesmo nas duas. Duplicado, ele deixou uma delas para trás na primeira vez que
// uma origem nova apareceu.
export function describeOrigin(origin: LegalDocumentOrigin | string): {
	label: string
	tone: 'success' | 'warn' | 'danger'
} {
	if (origin === 'oficial') {
		return { label: 'Documento oficial', tone: 'success' }
	}

	if (origin === 'web') {
		return { label: 'Capturada na web', tone: 'danger' }
	}

	return { label: 'Recorte do DLS', tone: 'warn' }
}

export interface LegalDocument {
	// O endereço da norma, e a chave por onde a busca a encontra.
	slug: string

	title: string
	kind: string
	origin: LegalDocumentOrigin | string
	status: string

	// Vazio quer dizer nacional: concorre em toda prova. Com escopo, só na
	// prova do lugar.
	scopes: string[]

	// Quantos dispositivos a norma tem. É o número que denuncia a norma que
	// entrou pela metade — um código com nove artigos é documento que o parser
	// não entendeu.
	provisionCount: number

	sourceUrl: string | null
	sourceFormat: string | null

	// Se o documento original ainda pode ser aberto para conferência.
	hasFile: boolean

	createdAt: string
}

// O relatório da carga, que é a resposta do próprio envio.
export interface LegalDocumentReport {
	family: 'norma' | 'sumulas' | 'julgados' | 'informativo' | 'lido' | 'emenda'
	slug: string | null
	provisions: number
	sumulas: number
	warnings: string[]
	pending: string | null
}

// O relatório como ele fica guardado: o que a carga entendeu, e o que decidiu.
export interface LegalDocumentLoadReport extends LegalDocumentReport {
	costUsd?: number
	fileName?: string
	title?: string
	scopes?: string[]
	nicknames?: string[]
	loadedAt?: string
}

// Contado na hora da consulta: `parada` é fila sem vetor novo há mais de dez minutos.
export interface LegalDocumentVectorization {
	state: 'concluida' | 'em-andamento' | 'parada'
	eligible: number
	pending: number
	lastEmbeddedAt: string | null
	summary: string
}

export function describeVectorization(
	state: LegalDocumentVectorization['state']
): { label: string; tone: 'success' | 'warn' | 'danger' } {
	if (state === 'concluida') {
		return { label: 'Concluída', tone: 'success' }
	}

	if (state === 'em-andamento') {
		return { label: 'Em andamento', tone: 'warn' }
	}

	return { label: 'Parada', tone: 'danger' }
}

// Os apelidos são o que faz a questão achar a lei sem citar o número: "CPC",
// "Lei de Registros Públicos". Sem apelido cadastrado, a busca direta só chega
// por citação exata, e a sigla passa a depender do que a conversão reconhecer
// pelo assunto da questão.
export interface LegalDocumentDetail extends LegalDocument {
	aliases: string[]

	// O relatório da carga que trouxe a norma, guardado junto da captura.
	//
	// Nulo em norma carregada antes de o relatório passar a ser gravado. Metade
	// dos avisos conta o que o microserviço decidiu no lugar de quem enviou — que
	// o título saiu da citação impressa, que a abrangência saiu da abertura — e
	// isso não se reconstitui relendo o documento. Quem quiser o de uma norma
	// antiga a reenvia.
	loadReport: LegalDocumentLoadReport | null

	vectorization?: LegalDocumentVectorization

	// A mudança mais recente: a carga, um dispositivo marcado ou emendado, ou a última emenda aplicada.
	updatedAt?: string
}

// Um dispositivo da norma, na lista da curadoria.
export interface LegalProvision {
	// O endereço relativo à norma: "art-5/inc-78/ali-a".
	path: string

	// artigo, paragrafo, inciso, alinea, item — e, fora da lei, enunciado, tese
	// e resumo.
	kind: string

	// O rótulo como a página o escreve: "Art. 6º", "XXIX", "a)".
	label: string

	status: string

	// Título, capítulo e seção de onde ele veio, de fora para dentro.
	hierarchy: string[]

	// O texto que vale hoje. Nulo quando todas as redações estão riscadas.
	text: string | null

	// Mais de uma quer dizer que houve emenda, e que a redação anterior está
	// guardada.
	redactionCount: number

	// Sem vetor a busca por sentido não o alcança: ele só é achado por citação
	// exata, e é assim que uma norma vetorizada pela metade se denuncia.
	embedded: boolean
}

export interface LegalRedaction {
	position: number
	text: string

	// Riscada na página oficial: é a redação que a emenda substituiu.
	struck: boolean

	current: boolean
	origin: string

	// A norma que mudou este texto, quando a citação da anotação foi resolvida.
	amendedBy: string | null

	annotation: string | null
}

export interface LegalProvisionDetail {
	norma: string
	path: string
	kind: string
	label: string
	status: string
	hierarchy: string[]
	notes: string[]
	embedded: boolean

	// Na ordem da página oficial. A vigente é a última não riscada; as outras são
	// o que a emenda substituiu, e quem confere questão antiga precisa vê-las.
	redactions: LegalRedaction[]
}

// As situações que a curadoria escreve.
//
// `revogado` fica de fora: a revogação é decisão do legislador e vem da própria
// página da norma, na carga. Escrevê-la à mão esconderia um defeito de
// segmentação em vez de corrigi-lo. `vigente` é o caminho de volta — a corte
// derruba e também restabelece.
export const CURATED_PROVISION_STATUSES = [
	'vigente',
	'inconstitucional',
	'suspenso',
] as const

export type CuratedProvisionStatus = (typeof CURATED_PROVISION_STATUSES)[number]

export function describeProvisionStatus(status: string): {
	label: string
	tone: 'success' | 'warn' | 'danger'
} {
	if (status === 'vigente') {
		return { label: 'Vigente', tone: 'success' }
	}

	if (status === 'revogado') {
		return { label: 'Revogado', tone: 'danger' }
	}

	if (status === 'inconstitucional') {
		return { label: 'Inconstitucional', tone: 'danger' }
	}

	return { label: 'Suspenso', tone: 'warn' }
}

export interface LegalDocumentListResponse {
	data: LegalDocument[]
	meta: Meta
}

export interface LegalDocumentListRequest {
	page?: number
	limit?: number
	search?: string
}

export interface LegalProvisionListRequest {
	id: string
	page?: number
	limit?: number

	// Serve a duas perguntas com a mesma caixa: "onde está o art. 5º" e "que
	// dispositivo fala de usucapião". Quem separa as duas é o microserviço.
	search?: string

	status?: string
}

export interface LegalProvisionListResponse {
	data: LegalProvision[]
	meta: Meta
}

export interface LegalDocumentCreateRequest {
	file: FileList

	// Como a norma é citada. Vira a chave dela no acervo, e é por ela que este
	// documento encontra e substitui o que a busca na internet deixou guardado.
	//
	// Em branco, sai da citação impressa na abertura do documento. Só a norma
	// sem número volta pedindo.
	title?: string

	nicknames?: string[]

	// A quem a norma se aplica. Vazio é nacional; em branco o microserviço
	// ainda tenta deduzir do próprio título.
	scopes?: string[]

	sourceUrl?: string
}

// Emenda estadual que a edição consolidada da norma ainda não traz. `id` nulo é a simulação.
export interface LegalAmendment {
	id: string | null
	number: number
	year: number
	promulgatedOn: string
	sourceUrl: string | null
	hasAct: boolean
	revoked: string[]
	origin: string | null
	lastReport: string | null
	lastAppliedAt: string | null
}

export type LegalAmendmentAction =
	| 'redacao-nova'
	| 'incluido'
	| 'revogado'
	| 'inalterado'

export function describeAmendmentAction(action: LegalAmendmentAction): {
	label: string
	tone: 'success' | 'danger' | 'info' | 'info2'
} {
	if (action === 'redacao-nova') {
		return { label: 'Redação nova', tone: 'info' }
	}

	if (action === 'incluido') {
		return { label: 'Incluído', tone: 'success' }
	}

	if (action === 'revogado') {
		return { label: 'Revogado', tone: 'danger' }
	}

	return { label: 'Já estava assim', tone: 'info2' }
}

export interface LegalAmendmentPlanEntry {
	path: string
	label: string
	action: LegalAmendmentAction
	text: string | null
}

export interface LegalAmendmentRegistration {
	simulated: boolean
	amendment: LegalAmendment
	plan: LegalAmendmentPlanEntry[]
	warnings: string[]
	pending: string | null
}

export interface LegalAmendmentRemoval {
	removed: LegalAmendment
	warnings: string[]
	pending: string
}

export interface LegalAmendmentRegisterRequest {
	id: string
	file: File
	number?: string
	year?: string
	promulgatedOn?: string
	sourceUrl?: string
	// Citados como o ato cita: "parágrafo único do art. 111".
	revoked: string[]
	simulate: boolean
}

export interface LegalAmendmentUpdateRequest {
	id: string
	amendmentId: string
	file?: File
	promulgatedOn?: string
	sourceUrl?: string
	revoked: string[]
	simulate: boolean
}
